import { styled } from '@material-ui/core';
import { ObjectUtils } from 'Helpers';
import { IRoom } from 'Plugins/FloorPlan/Models/IRoom';
import { PointUtils } from 'Plugins/FloorPlan/Utils/PointUtils';
import { CoordPoint } from 'Plugins/FloorPlan/Utils/Types';
import { SvgAttributesWith } from 'Plugins/FloorPlan/Widgets/SvgTypes';
import React, { SVGAttributes } from 'react';

import { BandType, LaizeBand, LaizeCalculatorResult } from '../../Laize';
import { LaizeBandItemV3 } from '../../v3/LaizeBandTrace';
import { useLaizeCalcDialog } from '../LaizeCalcDialogProvider';
import { toPolygonPoints } from '../Widgets/MapUILaizeTraces';
import {
    BandsDialog,
    calculateMapBoxWithPoints,
    generateMapBandsStyles,
    usePlanBandeFocusedBandId,
    useZoomLaizeRoomGesture,
} from './BandsDialog';
import {
    BandChip,
    BandsColorPalette,
    getLegendFocusStyle,
    getTextDimensions,
    indexToLetter,
    LaizeBandProps,
    LegendConstants,
    LegendItem,
    LegendMapTitle,
    LegendPositionProps,
    legendYOffset,
} from './Widget';
import { LaizeBands } from '../../v3/LaizeBands';
import { getProduitPrincipal } from 'Web/Pages/Quote/Utils/UpdateQuestions';

export const BandsFromBandIdDialog = () => {
    const { zoomedBands, setZoomedBands, state, laizeResult } = useLaizeCalcDialog();
    const band: LaizeBandItemV3 = laizeResult?.bands_v3?.find(
        (b) => b.bandId === zoomedBands?.bandId && b.type === BandType.New
    )!;

    return !band ? null : (
        <BandsDialog id="BandsFromBandIdDialog" open close={setZoomedBands}>
            {band && (
                <Container>
                    <BandsFromBandIdContent band={band} rooms={state.rooms || []} laizeResult={laizeResult!} />
                </Container>
            )}
        </BandsDialog>
    );
};
const Container = styled('div')({ position: 'relative', width: '100%', height: '100%', overflowY: 'auto' });

export const getImageBandName = (band: LaizeBandItemV3, rooms: IRoom[], laizeResult?: LaizeCalculatorResult) => {
    const bandNewList = laizeResult?.bands_v3?.filter((x) => x.type === BandType.New) || [];
    const index = bandNewList.findIndex((x) => x.id === band.id) + 1;
    const room = rooms.find((r) => r.roomId === band.roomId)!;

    const { produitPrincipal } = getProduitPrincipal(room);
    const productCode = produitPrincipal?.code;
    const imageName = `Bande ${index} - ${productCode}`;
    return { index, room, imageName, productCode };
};

const getBoundingMapPoints = (bands: LaizeBandItemV3[]) => {
    return bands.flatMap((band) => {
        switch (band.type) {
            case BandType.Reused:
                return [...(band.reusedRelativePoints || []), ...(band.reusedRelativePointsLaidSurface || [])];
            case BandType.New:
                return [...band.points, ...(band.pointsWithMargins || [])];
            default:
                return [];
        }
    });
};

type ContentProps = {
    setSvgWrapper?: (ref: React.RefObject<SVGSVGElement>) => void;
    band: LaizeBandItemV3;
    rooms: IRoom[];
    laizeResult: LaizeCalculatorResult;
    style?: React.CSSProperties;
    exportMode?: boolean;
};

const bandToDisplayTypes = [BandType.New, BandType.Reused];

export const BandsFromBandIdContent = ({
    setSvgWrapper,
    band,
    rooms,
    laizeResult,
    style,
    exportMode,
}: ContentProps) => {
    const svgWrapper = React.useRef<SVGSVGElement>(null);

    React.useEffect(() => {
        setSvgWrapper?.(svgWrapper);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [svgWrapper.current, band.bandId]);

    const bands: LaizeBandItemV3[] = (laizeResult?.bands_v3 || []).filter((b) => b.bandId === band.bandId);

    const roomIds = ObjectUtils.filterDuplicate(bands.map((b) => b.roomId));
    const bandDetails = laizeResult?.roomsBandsDetails?.find((x) => roomIds.includes(x.roomId))?.bands || [];
    const box = calculateMapBoxWithPoints(getBoundingMapPoints(bands));

    if (!box) return null;
    const { index, room } = getImageBandName(band, rooms, laizeResult);
    const { viewBox, legendTitle, legendItems, map } = generateMapBandsStyles(box, 16);
    const params: MapTracesProps = { bands, rooms, bandDetails, transform: map.transform, exportMode };

    return (
        <svg
            id={`BandsFromBandId-${band.id}`}
            ref={svgWrapper}
            xmlns="http://www.w3.org/2000/svg" //!\ IMPORTANT FOR EXPORT
            viewBox={`${viewBox.xMin} ${viewBox.yMin} ${viewBox.width} ${viewBox.height}`}
            width={viewBox.width}
            height={viewBox.height}
            style={style}>
            <MapBandTitle index={index} room={room} transform={legendTitle.transform} band={band} />
            <MapBandTraces {...params} />
            <MapBandLetter {...params} />
            <MapLegend {...params} transform={legendItems.transform} />
        </svg>
    );
};

const MapBandTitle = ({
    index,
    band,
    room,
    ...attrs
}: SvgAttributesWith<{ index: number; band: LaizeBandItemV3; room: IRoom }>) => {
    const { produitPrincipal } = getProduitPrincipal(room);
    const productCode = produitPrincipal?.code;
    const textDimensions = getTextDimensions({ width: band.bandWidth ?? 0, height: band.bandHeight ?? 0 });
    return (
        <g {...attrs}>
            <LegendMapTitle>
                Bande {index}
                {productCode && (
                    <tspan style={{ fontSize: 24, fontWeight: 400, fill: '#424448' }}> - {productCode}</tspan>
                )}
            </LegendMapTitle>
            <text y={32}>{textDimensions}</text>
        </g>
    );
};

type LaizeBandTraceProps = {
    points: CoordPoint[];
    fill?: string;
    dashArray?: boolean;
    stroke?: string;
    strokeWidth?: number;
};
type MapTracesProps = SvgAttributesWith<{
    bands: LaizeBandItemV3[];
    bandDetails: LaizeBand[];
    rooms: IRoom[];
    exportMode?: boolean;
}>;

//#region //* BANDES

const MapBandTraces = (props: MapTracesProps) => {
    const { bands, rooms, bandDetails, exportMode, ...attrs } = props;
    return (
        <g role="MapBandTraces" {...attrs}>
            {bands
                .filter((b) => bandToDisplayTypes.includes(b.type))
                .map((band, index) => {
                    const props: LaizeBandProps = { band, index, bandDetails, bands, rooms, exportMode };
                    switch (band.type) {
                        case BandType.New:
                            return <LaizeBandNew key={`LaizeBandNew-${index}`} {...props} />;
                        case BandType.Reused:
                            return <LaizeBandReused key={`LaizeBandReused-${index}`} {...props} />;
                        default:
                            return null;
                    }
                })}
        </g>
    );
};

const LaizeBandNew = ({ band, bands, exportMode }: SvgAttributesWith<LaizeBandProps>) => {
    const { focused, gestures } = useZoomLaizeRoomGesture(band.id);
    const pathBandLaidProps = toBandPathProps({
        points: band.points,
        fill: focused || exportMode ? BandsColorPalette.BandColorHighlight : BandsColorPalette.BandColor,
    });
    const pathBandProps = toBandPathProps({
        points: band.pointsWithMargins || [],
        //fill: focused ? BandsColorPalette.BandReusableHighlight : BandsColorPalette.BandReusable,
        fill: BandsColorPalette.BandReusable,
        strokeWidth: 0,
    });

    const bandSurfaceCovered = bands.find((b) => b.type === BandType.SurfaceCovered && b.roomId === band.roomId);
    const polygonProps: SVGAttributes<SVGPolygonElement> = {
        points: toPolygonPoints(bandSurfaceCovered?.pointsWithMargins || []),
        fill: 'transparent',
        stroke: '#13123A',
        strokeWidth: 1,
        strokeDasharray: '10,10',
    };

    return (
        <g id={`LaizeBandNew-${band.id}`}>
            <path {...pathBandProps} />
            <g {...gestures}>
                <path {...pathBandLaidProps} />
                <polygon {...polygonProps} />
            </g>
        </g>
    );
};

const LaizeBandReused = ({ band, exportMode }: SvgAttributesWith<LaizeBandProps>) => {
    const { focused, gestures } = useZoomLaizeRoomGesture(band.id);
    const pathProps = toBandPathProps({
        points: band.reusedRelativePointsLaidSurface || [],
        fill: focused || exportMode ? BandsColorPalette.BandColorHighlight : BandsColorPalette.BandColor,
    });
    const polygonWithMarginsProps = toBandMarginsProps({ points: band.reusedRelativePoints! });
    return (
        <g id={`LaizeBandReused-${band.id}`} {...gestures}>
            <path {...pathProps} />
            <polygon {...polygonWithMarginsProps} />
        </g>
    );
};

const toBandPathProps = ({
    points,
    fill,
    dashArray,
    stroke,
    strokeWidth,
}: LaizeBandTraceProps): React.SVGAttributes<any> => {
    const coords = points.map((p) => `${p.x},${p.y}`);
    return {
        d: `M${coords[0]} ${coords.map((coord) => `L${coord}`).join(' ')} L${coords[0]}`,
        stroke: stroke ?? BandsColorPalette.BandStroke,
        strokeWidth: strokeWidth ?? 2,
        strokeOpacity: 1,
        strokeDasharray: dashArray ? '3 3' : undefined,
        fill,
        fillRule: 'evenodd',
    };
};

const toBandMarginsProps = ({ points }: LaizeBandTraceProps): React.SVGAttributes<SVGPolygonElement> => {
    return {
        points: toPolygonPoints(points),
        fill: 'transparent',
        stroke: '#13123A',
        strokeWidth: 1,
        strokeDasharray: '10,10',
    };
};

const MapBandLetter = ({ bands, ...attrs }: SvgAttributesWith<{ bands: LaizeBandItemV3[] }>) => {
    return (
        <g role="MapBandLetter" {...attrs}>
            {bands
                .filter((b) => bandToDisplayTypes.includes(b.type))
                .map((band, index) => {
                    switch (band.type) {
                        case BandType.New:
                            return (
                                <LetterBandChipNew
                                    key={`BandLetter-${index}`}
                                    index={index}
                                    band={band}
                                    bands={bands}
                                />
                            );
                        case BandType.Reused:
                            return (
                                <LetterBandChip
                                    key={`BandLetter-${index}`}
                                    id={index}
                                    points={band.reusedRelativePoints || []}
                                />
                            );
                        default:
                            return null;
                    }
                })}
        </g>
    );
};

const LetterBandChipNew = ({
    index,
    band,
    bands,
}: {
    index: number;
    band: LaizeBandItemV3;
    bands: LaizeBandItemV3[];
}) => {
    const bandSurfaceCovered = bands.find((b) => b.type === BandType.SurfaceCovered && b.roomId === band.roomId);
    return (
        <LetterBandChip key={`BandLetter-${index}`} id={index} points={bandSurfaceCovered?.pointsWithMargins || []} />
    );
};

//#endregion

//#region //* LEGEND

const MapLegend = (props: MapTracesProps) => {
    const { bands, rooms, bandDetails, ...attrs } = props;
    let legendTextY = LegendConstants.LEGEND_TEXT_START_Y;
    const focusedBandId = usePlanBandeFocusedBandId();
    return (
        <g role="MapLegend" {...attrs}>
            {bands
                .filter((b) => bandToDisplayTypes.includes(b.type))
                .map((band, index) => {
                    const result = PreviewBandLegend({
                        band,
                        bands,
                        rooms,
                        index,
                        bandDetails,
                        posY: legendTextY,
                        focused: focusedBandId === band.id,
                    });
                    legendTextY += result.yOffset;
                    return <result.DrawLegendItem key={`LegendItem-${index}`} />;
                })}
        </g>
    );
};

export const PreviewBandLegend = (props: SvgAttributesWith<LaizeBandProps & LegendPositionProps>) => {
    const { index, band, rooms, bandDetails, bands, posY, focused } = props;
    const title = indexToLetter(index);
    const room = rooms.find((r) => r.roomId === band.roomId);
    const subtitle = `Découpe ${title} - ${room?.name}`;
    const dimens = getLegendDimensions({ band, bandDetails, bands, rooms });
    const styles = getLegendFocusStyle(band.type, focused, BandsColorPalette.BandColorHighlight);
    const yOffset = legendYOffset([dimens.legendText1, dimens.legendText2]);

    return {
        posY,
        yOffset,
        DrawLegendItem: () => (
            <LegendItem
                id={`LegendItem-cut-${band.id}`}
                posY={posY}
                title={title}
                subtitle={subtitle}
                text1={dimens.legendText1}
                text2={dimens.legendText2}
                fill={styles.fill}
            />
        ),
    };
};

const getLegendDimensions = ({ band, bandDetails, bands }: Omit<LaizeBandProps, 'index'>) => {
    let textWithMargins: string = '';
    let textWithoutMargins: string = '';

    if (band.type === BandType.New) {
        const correspondingBand = bandDetails.find((b) => b.id === band.bandId);
        textWithoutMargins = getTextDimensions(
            {
                width: band.cuttingBandWidth ?? 0,
                height: band.cuttingBandHeight ?? 0,
            }
        );

        if (correspondingBand?.surfaceCoveredCoords) {
            textWithMargins = getTextDimensions({
                width: band.bandToUseWidth ?? 0,
                height: band.bandToUseHeight ?? 0,
            });
        }
    }
    if (band.type === BandType.Reused) {
        textWithMargins = getTextDimensions(LaizeBands.getBandDimensions(band.points || []));
        textWithoutMargins = getTextDimensions(LaizeBands.getBandDimensions(band.reusedRelativePoints || []));
    }

    return {
        legendText1: `Découpe : ${textWithoutMargins}`,
        legendText2: `Bande à utiliser : ${textWithMargins}`,
    };
};

//#endregion;

const LetterBandChip = ({ id, points = [] }: React.PropsWithChildren<{ id: number; points: CoordPoint[] }>) => {
    const center = PointUtils.bboxCenter(points);
    return <BandChip transform={`translate(${center.x},${center.y}) scale(1)`}>{indexToLetter(id)}</BandChip>;
};
